import React from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface ErrorModalProps {
  open: boolean;
  title: string;
  message: string;
  onClose: () => void;
  onRetry?: () => void;
  actions?: { label: string; onClick: () => void | Promise<void> }[];
}

export function ErrorModal({ open, title, message, onClose, onRetry, actions }: ErrorModalProps) {
  if (!open) return null;
  
  return (
    <>
      {/* Overlay com animação */}
      <div 
        className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />
      
      {/* Modal com animação */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="
            w-full max-w-md
            bg-navy-800
            border border-red-500/30
            rounded-lg
            shadow-2xl shadow-red-500/20
            p-5
            text-gray-100
            pointer-events-auto
            animate-in zoom-in-95 slide-in-from-bottom-4 fade-in duration-200
          "
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h2 className="text-base font-semibold text-red-300">
                {title}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-navy-700 rounded transition-colors"
              aria-label="Fechar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Message */}
          <div className="text-sm text-gray-300 mb-5 leading-relaxed whitespace-pre-wrap pl-11">
            <p className="mb-2">{message}</p>
            {/* Separador visual se houver hint */}
            {message.includes('💡') && (
              <div className="mt-3 pt-3 border-t border-navy-700">
                <p className="text-xs text-gray-400 italic">
                  {message.split('💡')[1]?.trim()}
                </p>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex items-center justify-end gap-2 flex-wrap">
            {onRetry && (
              <button
                onClick={() => {
                  onRetry();
                  onClose();
                }}
                className="
                  px-4 py-2
                  text-sm font-medium
                  rounded-lg
                  bg-blue-600 hover:bg-blue-500
                  text-white
                  transition-colors
                  shadow-sm hover:shadow-md
                "
              >
                Tentar Novamente
              </button>
            )}
            {(actions || []).map((a, idx) => (
              <button
                key={idx}
                onClick={async () => {
                  await a.onClick();
                  onClose();
                }}
                className="
                  px-4 py-2
                  text-sm font-medium
                  rounded-lg
                  bg-red-600 hover:bg-red-500
                  text-white
                  transition-colors
                  shadow-sm hover:shadow-md
                "
              >
                {a.label}
              </button>
            ))}
            <button
              onClick={onClose}
              className="
                px-4 py-2
                text-sm font-medium
                rounded-lg
                bg-navy-700 hover:bg-navy-600
                text-gray-200
                transition-colors
              "
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </>
  );
}


